<template>
    <div id="status-attendance-page">
        <!-- HEADER -->
        <div class="action-header">
            <div class="search-container">
                <div class="search-input">
                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="search"
                        :placeholder="this.$vuetify.lang.t('$vuetify.statusAttendance.searchByEmployeeNoOrName')"
                        solo
                        class="subheader-margin"
                        height="40px"
                        @keyup.enter="filterAttendancesList()">
                    </v-text-field>
                </div>
                <div class="ml-3">
                    <v-btn
                        @click="filterAttendancesList()"
                        class="subheader-margin"
                        color="primary"
                        height="40px"
                        outlined>
                        {{ $vuetify.lang.t('$vuetify.statusAttendance.search')}}
                    </v-btn>
                </div>
                <div class="ml-day-inquiry">
                    <span class="date-picker-label">
                        {{ $vuetify.lang.t('$vuetify.statusAttendance.startDate') }}
                    </span>
                    <v-menu
                        v-model="startDatePickerShow"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="234px">
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                v-model="startDateFormatted"
                                prepend-inner-icon="event"
                                @click:prepend-inner="startDatePickerShow = true"
                                @click:clear="startDate = null"
                                readonly
                                v-on="on"
                                solo
                                height="40px"
                                background-color="#ffffff">
                            </v-text-field>
                        </template>
                        <v-date-picker
                            :locale="localeLang"
                            v-model="startDate"
                            @change="formatStartDate"
                            @input="startDatePickerShow = false">
                        </v-date-picker>
                    </v-menu>
                </div>
                <div class="ml-3">
                    <span class="date-picker-label">
                        {{ $vuetify.lang.t('$vuetify.statusAttendance.endDate') }}
                    </span>
                    <v-menu
                        v-model="endDatePickerShow"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="234px">
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                v-model="endDateFormatted"
                                :label="$vuetify.lang.t('$vuetify.statusAttendance.endDate')"
                                prepend-inner-icon="event"
                                @click:clear="endDate = null"
                                @click:prepend-inner="endDatePickerShow = true"
                                readonly
                                v-on="on"
                                solo
                                height="40px"
                                background-color="#ffffff">
                            </v-text-field>
                        </template>
                        <v-date-picker
                            :locale="localeLang"
                            v-model="endDate"
                            @change="formatEndDate"
                            @input="endDatePickerShow = false">
                        </v-date-picker>
                    </v-menu>
                </div>
                <div class="ml-3">
                    <v-select
                        @change="filterAttendancesList()"
                        v-model="attendanceStatus"
                        class="subheader-margin"
                        item-text="text"
                        item-value="value"
                        :items="statusList"
                        solo
                        height="40px"
                        background-color="#ffffff">
                    </v-select>
                </div>
            </div>
        </div>
        <!-- END HEADER -->

        <div class="note" v-if="isEISAttendance">{{ $vuetify.lang.t('$vuetify.statusAttendance.note') }}</div>

        <v-card class="content-container">
            <v-data-table
                :footer-props="{
                    'items-per-page-options': [10, 20, 30, 50, 100]
                }"
                @page-count="pageCount = $event"
                :page.sync="page"
                :headers="headers"
                :options.sync="options"
                :server-items-length="totalAttendance"
                :items="attendanceList"
                :loading="loading">
                <template v-slot:[`item.employee__fullname`]="{ item }">
                    <span :title="item.employee__fullname">
                        {{ item.employee__fullname }}
                    </span>
                </template>

                <template v-slot:[`item.statusIn`]="{ item }">
                    <span
                        v-if="item.statusIn"
                        :class="item.statusIn">
                        {{ item.statusIn.toUpperCase() }}
                    </span>
                    <span v-else>
                        -
                    </span>
                </template>

                <template v-slot:[`item.check_in_type`]="{ item }">
                    <span
                        v-if="item.check_in_type">
                        {{ item.check_in_type }}
                    </span>
                    <span v-else>
                        -
                    </span>
                </template>

                <template v-slot:[`item.checkin_time`]="{ item }">
                    <span
                        v-if="item.checkin_time && item.checkin_time != '00:00'">
                        {{ item.checkin_time }}
                    </span>
                    <span v-else>
                        -
                    </span>

                    <span @click="goToLocationDetails(item.check_in_location)" v-if="item.is_check_in_by_line && item.check_in_location">
                        <i class="fas pin-location fa-map-marker-alt"></i>
                    </span>
                    <v-avatar
                        v-else-if="item.checkin_time && item.faceIn"
                        class="pointer ml-3"
                        size="19"
                        @click="showImage(item.faceIn)">
                        <img
                            src="@/assets/icons/image.jpg">
                    </v-avatar>
                </template>

                <template v-slot:[`item.check_out_type`]="{ item }">
                    <span
                        v-if="item.check_out_type">
                        {{ item.check_out_type }}
                    </span>
                    <span v-else>
                        -
                    </span>
                </template>

                <template v-slot:[`item.checkout_time`]="{ item }">
                    <span
                        v-if="item.checkout_time && item.checkout_time != '00:00'">
                        {{ item.checkout_time }}
                    </span>
                    <span v-else>
                        -
                    </span>

                    <span @click="goToLocationDetails(item.check_out_location)" v-if="item.is_check_out_by_line && item.check_out_location">
                        <i class="fas pin-location fa-map-marker-alt"></i>
                    </span>
                    <v-avatar
                        v-else-if="item.checkout_time && item.faceOut"
                        class="pointer ml-3"
                        size="19"
                        @click="showImage(item.faceOut)">
                        <img
                            src="@/assets/icons/image.jpg">
                    </v-avatar>
                </template>

                <template v-slot:[`item.location_in_name`]="{ item }">
                    <span
                        :title="item.location_in_name"
                        v-if="item.location_in_name">
                        {{ item.location_in_name }}
                    </span>
                    <span v-else>-</span>
                </template>

                <template v-slot:[`item.location_out_name`]="{ item }">
                    <span
                        :title="item.location_out_name"
                        v-if="item.location_out_name">
                        {{ item.location_out_name }}
                    </span>
                    <span v-else>-</span>
                </template>
            </v-data-table>
        </v-card>

        <div
            v-if="pageCount"
            class="pt-2 paginator-box">
            <div>
                <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </div>
        </div>
        <image-preview
            ref="imagePreview" />
    </div>
</template>

<script>
// import permissionConstant from '@/constants/permissions.js'
import ImagePreview from '@/components/ImagePreview'
export default {
    name: 'StatusAttendance',

    components: {
        ImagePreview
    },

    data () {
        return {
            loading: false,
            totalAttendance: 0,
            attendanceList: [],
            pageCount: 1,
            page: 1,
            options: {},
            search: '',
            startDate: this.$util.getCurrentDate(),
            endDate: this.$util.getCurrentDate(),
            startDatePickerShow: false,
            endDatePickerShow: false,
            startDateFormatted: '',
            endDateFormatted: '',
            attendanceStatus: null,
            attendanceStatusItem: [],
            exportForm: {
                search: null,
                startDate: null,
                endDate: null,
                attendanceStatus: null
            },
            branch: null,
            isEISAttendance: process.env.VUE_APP_EIS_ATTENDANCE
        }
    },

    computed: {
        localeLang () {
            return this.$vuetify.lang.current
        },

        headers () {
            if (this.isEISAttendance) {
                return [
                    { text: this.$vuetify.lang.t('$vuetify.statusAttendance.employeeNo'), value: 'employee__employee_no', width: '10%' },
                    { text: this.$vuetify.lang.t('$vuetify.statusAttendance.name'), value: 'employee__fullname', width: '15%' },
                    { text: this.$vuetify.lang.t('$vuetify.statusAttendance.date'), value: 'date', width: '12%' },
                    { text: this.$vuetify.lang.t('$vuetify.statusAttendance.terminalIn'), value: 'location_in_name', width: '12.5%' },
                    { text: this.$vuetify.lang.t('$vuetify.statusAttendance.ciTime'), value: 'checkin_time', width: '10%' },
                    { text: this.$vuetify.lang.t('$vuetify.statusAttendance.terminalOut'), value: 'location_out_name', width: '12.5%' },
                    { text: this.$vuetify.lang.t('$vuetify.statusAttendance.coTime'), value: 'checkout_time', width: '10%' },
                    { text: this.$vuetify.lang.t('$vuetify.statusAttendance.totalHours'), value: 'total_hours', width: '10%', sortable: false },
                    { text: this.$vuetify.lang.t('$vuetify.statusAttendance.status'), value: 'status', width: '10%' }
                ]
            }
            return [
                { text: this.$vuetify.lang.t('$vuetify.statusAttendance.employeeNo'), value: 'employee__employee_no', width: '10%' },
                { text: this.$vuetify.lang.t('$vuetify.statusAttendance.name'), value: 'employee__fullname', width: '18%' },
                { text: this.$vuetify.lang.t('$vuetify.statusAttendance.date'), value: 'date', width: '12%' },
                { text: this.$vuetify.lang.t('$vuetify.statusAttendance.status'), value: 'status', width: '11%' },
                { text: this.$vuetify.lang.t('$vuetify.statusAttendance.ciType'), value: 'check_in_type', width: '12%' },
                { text: this.$vuetify.lang.t('$vuetify.statusAttendance.ciTime'), value: 'checkin_time', width: '12%' },
                { text: this.$vuetify.lang.t('$vuetify.statusAttendance.coType'), value: 'check_out_type', width: '12%' },
                { text: this.$vuetify.lang.t('$vuetify.statusAttendance.coTime'), value: 'checkout_time', width: '12%' }
            ]
        },
        statusList () {
            const status = this.attendanceStatusItem
            status.unshift({
                text: this.$vuetify.lang.t('$vuetify.statusAttendance.allStatus'),
                value: null
            })
            return status
        }
    },
    methods: {
        showImage (path) {
            this.$refs.imagePreview.toggleShow(path)
        },
        async getAttendancesList () {
            this.loading = true
            const { sortBy, sortDesc, page, itemsPerPage } = this.options

            let url = `attendances/attendance-status?branch=${this.branch}&page=${page}&itemsPerPage=${itemsPerPage}`
            if (sortBy && sortBy[0]) {
                url += `&sortBy=${sortBy[0]}`
            }

            if (sortDesc && sortDesc[0]) {
                url += `&sortDesc=${sortDesc[0]}`
            }

            if (this.search) {
                url += `&search=${this.search}`
            }

            if (this.startDate) {
                url += `&startDate=${this.startDate}`
            }

            if (this.endDate) {
                url += `&endDate=${this.endDate}`
            }

            if (this.attendanceStatus) {
                url += `&attendanceStatus=${this.attendanceStatus}`
            }
            const res = await this.axios.get(url)
            if (res && res.data && res.data.status === 'ok') {
                this.attendanceList = res.data.data
                this.totalAttendance = res.data.total
                this.loading = false
            }
        },

        formatStartDate () {
            this.startDateFormatted = this.$util.formatDate(this.startDate)
        },

        formatEndDate () {
            this.endDateFormatted = this.$util.formatDate(this.endDate)
        },

        async getBranch () {
            this.branch = localStorage.getItem('currentBranchId')
            if (this.branch) {
                await this.getAttendancesList()
            }
        },

        async getAttendanceStatus () {
            const res = await this.axios.get('attendance/status')
            if (res && res.data && res.data.status === 'ok') {
                this.attendanceStatusItem = res.data.attendance_status
            }
        },

        async filterAttendancesList () {
            this.options.page = 1
            await this.getAttendancesList()
        },
        goToLocationDetails (checkInLocationId) {
            this.$router.push({ name: 'CheckInLocationDetail', params: { id: checkInLocationId } }, () => {})
        }

    },

    async mounted () {
        await this.getBranch()
        await this.getAttendanceStatus()
        this.formatStartDate()
        this.formatEndDate()
    },

    watch: {
        options: {
            async handler () {
                if (this.branch) {
                    await this.getAttendancesList()
                }
            },
            deep: true
        },
        '$vuetify.lang.current': {
            handler: async function (after, before) {
                if (this.branch) {
                    await this.getAttendancesList()
                }
                await this.getAttendanceStatus()
            },
            deep: true
        }
    }
}
</script>

<style lang="scss" scoped>
::v-deep .v-input__control > .v-input__slot {
    min-height: auto !important;
    display: flex !important;
    align-items: center !important;
}
::v-deep .search-input .v-input__control > .v-input__slot {
    box-shadow: none !important;
    border: 1px solid #a8a8a8 !important;
}

.late {
    color: #d76436;
}
.present {
    color: #4caf50;
}
.absent {
    color: #ff5252;
}
.pointer {
    cursor: pointer;
}
.date-picker-label {
    font-size: 12px;
    color: #757575;
}
.subheader-margin {
    margin-top: 24px;
}
.action-header {
    margin-top: 0.5rem;
}
.pin-location {
    color: #d76436;
    margin-left: 5px;
    cursor: pointer;
}
.ml-day-inquiry {
    margin-left: 40px;
}
.note {
    font-size: 12px;
    margin-bottom: 5px;
}
</style>
